// Ward medication requests (multi-store Phase 4) — contract §6r.
// The ward side lives under /api/ip (ip:med-request to raise/cancel); the
// pharmacy side under /api/ip-pharmacy (pharmacy:outpatient).
import { api } from '@/lib/api/client';

export type WardRequestStatus = 'requested' | 'dispensed' | 'collected' | 'cancelled';
export type WardRequestPriority = 'routine' | 'urgent' | 'stat';

export interface WardRequestItem {
  id: number;
  request_id: number;
  medication_order_id: number;
  medicine_id: number | null;
  drug_name: string;
  dose_quantity: string | null;
  dose_unit: string | null;
  frequency_code: string | null;
  route: string | null;
  order_status: string;
  qty_requested: number;
  qty_dispensed: number;
  short_reason: string | null;
}

export interface WardRequest {
  id: number;
  request_number: string;
  admission_id: number;
  patient_id: number;
  patient_name: string;
  uhid: string;
  ward_name: string | null;
  bed_number: string | null;
  status: WardRequestStatus;
  priority: WardRequestPriority;
  notes: string | null;
  requested_by: string | null;
  requested_at: string;
  bill_id: number | null;
  bill_number: string | null;
  dispensed_by: string | null;
  dispensed_at: string | null;
  collected_by: string | null;
  handed_over_by: string | null;
  collected_at: string | null;
  cancelled_by: string | null;
  cancelled_at: string | null;
  cancel_reason: string | null;
  items: WardRequestItem[];
}

export interface UnsuppliedOrder {
  medication_order_id: number;
  admission_id: number;
  drug_name: string;
  medicine_id: number | null;
  dose_quantity: string | null;
  dose_unit: string | null;
  frequency_code: string | null;
  route: string | null;
  is_prn: boolean;
  start_datetime: string;
  patient_name: string;
  uhid: string;
  ward_name: string | null;
  bed_number: string | null;
}

export interface Worklist {
  requests: WardRequest[];
  ready: WardRequest[];
  unsupplied: UnsuppliedOrder[];
}

export interface DispenseRequestInput {
  lines: Array<{
    item_id: number;
    batches: Array<{ inventory_id: number; quantity: number; batch_selection_reason?: string; batch_selection_remarks?: string }>;
    short_reason?: string;
  }>;
}

// ── Ward ─────────────────────────────────────────────────────────
export function fetchAdmissionRequests(admissionId: number): Promise<WardRequest[]> {
  return api.get(`/ip/admissions/${admissionId}/medication-requests`);
}

export function createWardRequest(admissionId: number, input: {
  priority: WardRequestPriority; notes?: string; items: Array<{ medication_order_id: number; quantity: number }>;
}): Promise<WardRequest> {
  return api.post(`/ip/admissions/${admissionId}/medication-requests`, input);
}

export function cancelWardRequest(id: number, reason: string): Promise<WardRequest> {
  return api.post(`/ip/medication-requests/${id}/cancel`, { reason });
}

// ── Pharmacy ─────────────────────────────────────────────────────
export function fetchWorklist(): Promise<Worklist> {
  return api.get('/ip-pharmacy/worklist');
}

export function dispenseWardRequest(id: number, input: DispenseRequestInput): Promise<{ request: WardRequest; bill: { id: number; bill_number: string }; quantity_flags: unknown[] }> {
  return api.post(`/ip-pharmacy/requests/${id}/dispense`, input);
}

export function markWardRequestCollected(id: number, collectedBy: string): Promise<WardRequest> {
  return api.post(`/ip-pharmacy/requests/${id}/collected`, { collected_by: collectedBy });
}
